import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import ThirdSection from '../../components/try-demo/third-section';
import { useSessionMetadataStore } from '../../zustand/session-metadata-store';
import { ensureSessionExitUrlsFetched } from '../../utilis/session-exit-urls';
import tw from 'twin.macro';
import { css } from 'styled-components';

// 100vh is the iOS Safari *static* viewport — taller than what's actually
// visible with the toolbar shown, which pushed the Exercise Now button below
// the fold and forced a page scroll to reach it. dvh tracks the visible
// viewport; the vh line is the fallback for engines without dvh and must stay
// a separate declaration, since a `tw` class pair collapses into one height key.
const pageHeight = css`
  height: 100vh;
  height: 100dvh;
`;

function safeParseJson(value, fallback = null) {
  if (typeof value !== 'string' || value.trim() === '') {
    return fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

function syncPrescriptionExercise(sessionId) {
  // Read once and carry completeUrl/exitUrl (see utilis/session-exit-urls.js)
  // through both branches below — camera-permission's fetch may well have
  // already written them into initialConfig by the time this runs, and the
  // prescription branch used to rebuild the object from scratch with no
  // spread, silently dropping them.
  const storedInitialConfig = safeParseJson(localStorage.getItem('initialConfig'), {});
  const { completeUrl = null, exitUrl = null } = storedInitialConfig ?? {};

  const exercises = safeParseJson(localStorage.getItem('prescriptionExercises'), []);
  if (!Array.isArray(exercises) || exercises.length === 0) {
    if (sessionId && storedInitialConfig?.sessionId !== sessionId) {
      localStorage.setItem('initialConfig', JSON.stringify({ ...storedInitialConfig, sessionId }));
    }
    return;
  }

  const shouldAdvance = Boolean(localStorage.getItem('skipInstruction'));
  const currentIndex = Number(localStorage.getItem('prescriptionExerciseIndex') || 0);
  const nextIndex = Math.min(shouldAdvance ? currentIndex + 1 : currentIndex, exercises.length - 1);
  const exercise = exercises[nextIndex];

  if (shouldAdvance) {
    localStorage.removeItem('skipInstruction');
  }

  localStorage.setItem('prescriptionExerciseIndex', String(nextIndex));
  localStorage.setItem(
    'initialConfig',
    JSON.stringify({
      settings: exercise?.settings ?? {},
      sessionId,
      completeUrl,
      exitUrl,
    })
  );
  localStorage.setItem('exerciseName', exercise?.name || '');
  localStorage.setItem('shadow_url', exercise?.shadowUrl || '');
  localStorage.setItem('isFlip', exercise?.isFlip || false);
  localStorage.setItem('exerciseId', exercise?.id || '');
}

const TrayDemo = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sid') || searchParams.get('sessionId');
  const voiceCompanionMode = searchParams.get('voiceCompanionMode');
  // Relayed onto /exercise below — see api-instance.js and apis/realtime/api.js,
  // which read it straight off the URL on that page.
  const sessionToken = searchParams.get('session_token');
  // Same relay, optional — lands in the recorded video's filename on /exercise.
  const patientName = searchParams.get('patientName');

  useEffect(() => {
    syncPrescriptionExercise(sessionId);
  }, [sessionId]);

  // Usually already loaded by /camera-permission by the time a patient reaches
  // this page — this call is what covers a deep-link straight onto /try-demo,
  // and the store dedupes against the earlier fetch by session id either way.
  useEffect(() => {
    useSessionMetadataStore.getState().fetchMetadata(sessionId);
  }, [sessionId]);

  // Usually already in flight from /camera-permission by the time a patient
  // reaches this page — ensureSessionExitUrlsFetched dedupes by session id,
  // so this either reuses that fetch or (a deep link straight onto
  // /try-demo) starts it fresh. Kept on a ref so navigateToExercise below can
  // wait on the exact same promise instead of firing a second request.
  const sessionExitUrlsFetchRef = useRef(null);
  useEffect(() => {
    sessionExitUrlsFetchRef.current = ensureSessionExitUrlsFetched(sessionId);
  }, [sessionId]);

  const navigateToExercise = useCallback(async () => {
    const postData = localStorage.getItem('is_set_finish');
    if (postData) {
      localStorage.removeItem('is_set_finish');
      localStorage.removeItem('exercise_timer_finish');
    }

    localStorage.removeItem('skipInstruction');

    // Give the completeUrl/exitUrl fetch a chance to land in initialConfig
    // before /exercise reads it — otherwise a patient who taps "Exercise Now"
    // right away can beat that network round trip, and /exercise (no retry
    // of its own) is left with completeUrl/exitUrl unset for the rest of the
    // session. Capped so a slow or dead endpoint never stalls the transition
    // itself — past the cap this just proceeds with whatever initialConfig
    // already has, same as before this endpoint existed.
    await Promise.race([
      sessionExitUrlsFetchRef.current ?? Promise.resolve(),
      new Promise(resolve => setTimeout(resolve, 3000)),
    ]);

    const exerciseParams = new URLSearchParams();
    if (voiceCompanionMode) {
      exerciseParams.set('voiceCompanionMode', voiceCompanionMode);
    }
    if (sessionToken) {
      exerciseParams.set('session_token', sessionToken);
    }
    if (patientName) {
      exerciseParams.set('patientName', patientName);
    }
    const query = exerciseParams.toString();
    navigate(query ? `/exercise?${query}` : '/exercise', { replace: true });
  }, [navigate, sessionId, voiceCompanionMode, sessionToken, patientName]);

  // No page-level timeout: ThirdSection drives the transition to the exercise
  // once its LAST image slide finishes. That way, if the user steps back to an
  // earlier slide, the transition is deferred until the last image plays out
  // again — instead of firing on a fixed elapsed time.
  return (
    <div css={[tw`grid overflow-hidden relative grid-cols-2 w-screen bg-white`, pageHeight]}>
      <div
        css={[tw`col-span-2 w-screen overflow-hidden`, pageHeight]}
        style={{ animation: 'trydemo-section-fade 0.75s ease-out both' }}
      >
        <ThirdSection onExerciseNow={navigateToExercise} />
      </div>
    </div>
  );
};
export default TrayDemo;
